import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  differenceInCalendarDays,
  format,
  formatDistanceToNowStrict,
  isThisYear,
  parseISO,
} from 'date-fns';
import { de } from 'date-fns/locale';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The content is German and the team is German, so dates are formatted with
 * the German locale. Kept in one place so it's a one-line change if that ever
 * needs to be per-user.
 */
const locale = de;

export function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = parseISO(iso);
  return format(d, isThisYear(d) ? 'd. MMM' : 'd. MMM yyyy', { locale });
}

export function formatDateLong(iso: string | null): string {
  if (!iso) return '';
  return format(parseISO(iso), 'EEEE, d. MMMM yyyy', { locale });
}

export function formatRelative(iso: string | null): string {
  if (!iso) return '';
  return formatDistanceToNowStrict(parseISO(iso), { addSuffix: true, locale });
}

export type DueStatus = 'overdue' | 'today' | 'tomorrow' | 'soon' | 'later' | 'none';

/**
 * Buckets a due date for colour-coding. Uses calendar-day differences, not
 * elapsed hours, so "today" means today regardless of the time of day.
 */
export function dueStatus(iso: string | null, done = false): DueStatus {
  if (!iso || done) return 'none';
  const days = differenceInCalendarDays(parseISO(iso), new Date());
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days <= 7) return 'soon';
  return 'later';
}

export function dueLabel(iso: string | null, done = false): string {
  const status = dueStatus(iso, done);
  switch (status) {
    case 'today':
      return 'Heute';
    case 'tomorrow':
      return 'Morgen';
    case 'overdue': {
      const days = Math.abs(differenceInCalendarDays(parseISO(iso as string), new Date()));
      return days === 1 ? '1 Tag überfällig' : `${days} Tage überfällig`;
    }
    default:
      return formatDate(iso);
  }
}

export const DUE_CLASSES: Record<DueStatus, string> = {
  overdue: 'text-red-600 dark:text-red-400 font-medium',
  today: 'text-orange-600 dark:text-orange-400 font-medium',
  tomorrow: 'text-amber-600 dark:text-amber-400',
  soon: 'text-body',
  later: 'text-muted',
  none: 'text-muted',
};

/** Vikunja's priority scale. 0 means unset and is never displayed. */
export const PRIORITY_LABELS: Record<number, string> = {
  1: 'Niedrig',
  2: 'Mittel',
  3: 'Hoch',
  4: 'Dringend',
  5: 'JETZT',
};

export const PRIORITY_CLASSES: Record<number, string> = {
  1: 'text-slate-500 dark:text-slate-400',
  2: 'text-yellow-600 dark:text-yellow-500',
  3: 'text-orange-600 dark:text-orange-400',
  4: 'text-red-600 dark:text-red-400',
  5: 'text-rose-700 dark:text-rose-400 font-semibold',
};

/** Initials for an avatar. Handles single names and multi-word names. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return ((parts[0] as string)[0] ?? '') + ((parts[parts.length - 1] as string)[0] ?? '');
}

/**
 * Deterministic avatar colour from a user id, so the same person is always the
 * same colour without storing anything.
 */
const AVATAR_COLOURS = [
  'bg-emerald-600', 'bg-teal-600', 'bg-cyan-700', 'bg-sky-700',
  'bg-indigo-600', 'bg-violet-600', 'bg-fuchsia-700', 'bg-rose-600',
  'bg-orange-600', 'bg-amber-700', 'bg-lime-700', 'bg-green-700',
];

export function avatarColour(id: number): string {
  return AVATAR_COLOURS[Math.abs(id) % AVATAR_COLOURS.length] as string;
}

/**
 * Readable text colour for an arbitrary background, used for label chips whose
 * colour comes from the database. Uses the WCAG relative-luminance formula
 * rather than a naive brightness average.
 */
export function contrastText(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#ffffff';
  const channel = (i: number) => {
    const v = Number.parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.179 ? '#111827' : '#ffffff';
}

export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
